'use strict';

require('./KayleeService');
require('./RoomPasswordService');
require('./RoomService');
var log = require('../../log/Log').channel('ViewStateService');
var defaults = require('../../defaults/Defaults');
var moment = require('moment');
var objectToArray = require('./ObjectToArray');
var sutl = require('../../sutl/Sutl');

angular.module('inara').factory('ViewStateService', ViewStateService);

ViewStateService.$inject = [
    '$rootScope',
    '$q',
    'RoomService',
    'KayleeService',
    'CallsService',
    '$timeout',
    '$interval',
    '$state',
    'Idle',
    'RoomPasswordService',
];

function ViewStateService(
    $rootScope,
    $q,
    RoomService,
    KayleeService,
    CallsService,
    $timeout,
    $interval,
    $state,
    Idle,
    RoomPasswordService
) {
    var room = null;
    var site = null;
    var company = null;
    var calendar = null;
    var calendarEvents = null;
    var controlStateInitialized = false;
    var remoteControlSessions = null;
    var inCallState = defaults.inara.inCallView;
    var inCallStateListeners = [];
    var noCallState = defaults.inara.noView;
    var noCallStateListeners = [];
    var previousRemoteControlSessionCount = 0;
    var nextCalendarEventId = null;
    var nextCalendarEventInterval = null;
    var nextCalendarEventCheckDurationMs = 60 * 1000;
    var timeBeforeDialAllowed = moment.duration(defaults.inara.calendarTimeBeforeDialAllowedMs);

    log.info('initializing');

    RoomService.onOnlineChanged(onRoomServiceOnlineChanged);

    $rootScope.$on('RoomPasswordService', onRoomPasswordService);

    return {
        getControlStateInitialized: getControlStateInitialized,
        getCurrentState: getCurrentState,
        getInitialState: getInitialState,
        getNoCallState: getNoCallState,
        getInCallState: getInCallState,
        onInCallStateChanged: onInCallStateChanged,
        offInCallStateChanged: offInCallStateChanged,
        onNoCallStateChanged: onNoCallStateChanged,
        offNoCallStateChanged: offNoCallStateChanged,
        setControlStateInitialized: setControlStateInitialized,
    };

    function addListener(listeners, listener, currentValue) {
        listeners.push(listener);
        listener(currentValue);
    }

    function anyRemoteControlItemVisible() {
        var showCode = remoteControlCodeVisible();
        var showUsers = remoteControlUsersVisible();
        return showCode || showUsers;
    }

    function canIdleAudioSource() {
        var audioInputSources = sutl.getNode(room, 'configuredCaps.audioInputSources');
        return audioInputSources !== null && Object.keys(audioInputSources).length > 0;
    }

    function canIdleCalendar() {
        return room !== null && sutl.getNode(room, 'caps.hasCalendar', false);
    }

    function canIdleCamera() {
        var cameraSwitchers = sutl.getNode(room, 'configuredCaps.cameraSwitchers');
        return (
            cameraSwitchers !== null &&
            objectToArray(cameraSwitchers).filter(function (cameraSwitcher) {
                return !cameraSwitcher.isHidden;
            }).length > 0
        );
    }

    function canIdleDialpad() {
        return (
            CallsService.hasConfiguredCallCapsClass('audio') ||
            CallsService.hasConfiguredCallCapsClass('video')
        );
    }

    function canIdleDirectory() {
        return room !== null && sutl.getNode(room, 'caps.hasDirectory');
    }

    function canIdleDisplay() {
        var displays = sutl.getNode(room, 'configuredCaps.displays');
        return (
            displays !== null &&
            objectToArray(displays).filter(function (display) {
                return !display.isHidden;
            }).length > 0
        );
    }

    function canIdleLighting() {
        var lightingControls = sutl.getNode(room, 'configuredCaps.lightingControls');
        var lightingPresets = sutl.getNode(room, 'configuredCaps.lightingPresets');
        var shadeControls = sutl.getNode(room, 'configuredCaps.shadeControls');
        var shadePresets = sutl.getNode(room, 'configuredCaps.shadePresets');
        var hasShadeControls = false;
        if (shadeControls !== null) {
            for (var key in shadeControls) {
                if (!shadeControls.hasOwnProperty(key)) continue;
                if (
                    (shadeControls[key].showPositionControls && shadeControls[key].position) ||
                    (shadeControls[key].showTiltControls && shadeControls[key].tilt)
                ) {
                    hasShadeControls = true;
                    break;
                }
            }
        }
        return (
            (lightingControls !== null && Object.keys(lightingControls).length > 0) ||
            (lightingPresets !== null && Object.keys(lightingPresets).length > 0) ||
            (shadePresets !== null && Object.keys(shadePresets).length > 0) ||
            hasShadeControls
        );
    }

    function canIdleRemoteControl() {
        return (
            sutl.getNode(site, 'settings.remoteControlEnabled') === true &&
            KayleeService.isConnected() &&
            ((KayleeService.isAuthenticatedAsUser() &&
                !KayleeService.isAuthenticatedAsAnonymousRemoteControl()) ||
                KayleeService.isAuthenticatedAsController())
        );
    }

    function checkNextCalendarEvent() {
        if (calendarEvents === null) return;
        var event;
        var now = moment();

        for (var i = 0; i < calendarEvents.length; i++) {
            event = calendarEvents[i];
            if (event.$id !== nextCalendarEventId && !event.isAllDay) {
                var eventStart = moment(event.start);
                var eventEnd = moment(event.end);
                if (now.isBetween(eventStart.subtract(timeBeforeDialAllowed), eventEnd)) {
                    log.debug('Setting next event to: ' + event.$id + ' "' + event.summary + '"');
                    nextCalendarEventId = event.$id;
                    break;
                }
            }
        }
    }

    function getControlStateInitialized() {
        return controlStateInitialized;
    }

    function getCurrentState() {
        return CallsService.getActiveCalls().length > 0 ? getInCallState() : getInitialState();
    }

    function getStateFromIdleScreenSetting(idleScreen) {
        var state = defaults.inara.noView;
        switch (idleScreen) {
            case 'dialpad':
                if (canIdleDialpad()) state = defaults.inara.dialPadView;
                break;
            case 'calendar':
                if (canIdleCalendar()) state = defaults.inara.calendarView;
                break;
            case 'camera':
                if (canIdleCamera()) state = defaults.inara.cameraView;
                break;
            case 'directory':
                if (canIdleDirectory()) state = defaults.inara.directoryView;
                break;
            case 'display':
                if (canIdleDisplay()) state = defaults.inara.displayView;
                break;
            case 'lighting':
                if (canIdleLighting()) state = defaults.inara.lightingView;
                break;
            case 'audio_source':
                if (canIdleAudioSource()) state = defaults.inara.audioSourceView;
                break;
            case 'remote_control':
                if (canIdleRemoteControl()) state = defaults.inara.remoteControlView;
                break;
        }
        return state;
    }

    function getInitialState() {
        return getNoCallState();
    }

    function getNoCallState() {
        processNoCallState();
        return noCallState;
    }

    function getInCallState() {
        return inCallState;
    }

    function notifyListeners(listeners, value) {
        // Build a temporary list of listeners to loop over in case
        // someone adds or removes a listener in their handler.

        var tempListeners = [];
        var i;

        for (i = 0; i < listeners.length; i++) {
            tempListeners.push(listeners[i]);
        }

        for (i = 0; i < tempListeners.length; i++) {
            tempListeners[i](value);
        }
    }

    function offInCallStateChanged(listener) {
        removeListener(inCallStateListeners, listener);
    }

    function offNoCallStateChanged(listener) {
        removeListener(noCallStateListeners, listener);
    }

    function onCalendarChanged() {
        calendarEvents = objectToArray(angular.copy(calendar.events)).sort(function (a, b) {
            var aStart = a.isAllDay ? a.end : a.start;
            var bStart = b.isAllDay ? b.end : b.start;
            return aStart - bStart;
        });
        registerNextCalendarEventCheck();
    }

    function onInCallStateChanged(listener) {
        addListener(inCallStateListeners, listener, inCallState);
    }

    function onNoCallStateChanged(listener) {
        addListener(noCallStateListeners, listener, noCallState);
    }

    function onNodeChanged() {
        if (processNoCallState()) notifyListeners(noCallStateListeners, noCallState);
        registerNextCalendarEventCheck();
    }

    function onRemoteControlSessionRemoved(sessionId) {
        if (!anyRemoteControlItemVisible() && processNoCallState()) {
            notifyListeners(noCallStateListeners, noCallState);
        }
    }

    function onRoomPasswordService(event, msg) {
        switch (msg) {
            case 'locked':
            case 'unlocked':
                onRoomServiceOnlineChanged(RoomService.online() && RoomService.getRoom() !== null);
                if (processInCallState()) notifyListeners(inCallStateListeners, inCallState);
                if (processNoCallState()) notifyListeners(noCallStateListeners, noCallState);
                break;
            default:
                log.debug('Unknown RoomPasswordService message');
                break;
        }
    }

    function onRoomServiceOnlineChanged(online) {
        if (online) {
            room = RoomService.getRoom();
            room.$onChange(onNodeChanged);
            remoteControlSessions = room.$getRemoteControlSessions();
            remoteControlSessions.$onChildRemoved(onRemoteControlSessionRemoved);
            calendar = room.$getCalendar();
            calendar.$onChange(onCalendarChanged);
            site = KayleeService.getSite(room.siteId);
            site.$onChange(onNodeChanged);
            company = KayleeService.getCompany(room.companyId);
            company.$onChange(onNodeChanged);
        } else {
            if (room !== null) {
                room.$offChange(onNodeChanged);
                room = null;
            }
            if (remoteControlSessions !== null) {
                remoteControlSessions.$offChildRemoved(onRemoteControlSessionRemoved);
                remoteControlSessions = null;
            }
            if (calendar !== null) {
                calendar.$offChange(onCalendarChanged);
                calendar = null;
            }
            if (site !== null) {
                site.$offChange(onNodeChanged);
                site = null;
            }
            if (company !== null) {
                company.$offChange(onNodeChanged);
                company = null;
            }
            nextCalendarEventId = null;
        }
        registerNextCalendarEventCheck(online);
    }

    function processInCallState() {
        var lastInCallState = inCallState;
        inCallState = RoomPasswordService.isLocked()
            ? defaults.inara.roomPasswordView
            : defaults.inara.inCallView;
        return lastInCallState !== inCallState;
    }

    function processNoCallState() {
        var lastNoCallState = noCallState;

        // process manual idle screen
        var idleScreen = sutl.getNode(site, ['settings', 'idleScreen']);
        if (idleScreen !== null) {
            if (RoomPasswordService.isLocked()) {
                noCallState = defaults.inara.roomPasswordView;
            } else {
                noCallState = getStateFromIdleScreenSetting(idleScreen);
            }
            if (noCallState !== defaults.inara.noView) {
                // manual state was valid, return it
                return noCallState !== lastNoCallState;
            }
            // else, continue processing automatic idle screen
        }

        // process automatic idle screen
        if (RoomPasswordService.isLocked()) {
            noCallState = defaults.inara.roomPasswordView;
        } else if (canIdleCalendar()) {
            noCallState = defaults.inara.calendarView;
        } else if (canIdleDirectory()) {
            noCallState = defaults.inara.directoryView;
        } else if (canIdleDialpad()) {
            noCallState = defaults.inara.dialPadView;
        } else if (canIdleDisplay()) {
            noCallState = defaults.inara.displayView;
        } else if (canIdleLighting()) {
            noCallState = defaults.inara.lightingView;
        } else if (canIdleAudioSource()) {
            noCallState = defaults.inara.audioSourceView;
        } else if (canIdleCamera()) {
            noCallState = defaults.inara.cameraView;
        } else if (canIdleRemoteControl()) {
            noCallState = defaults.inara.remoteControlView;
        } else {
            noCallState = defaults.inara.noView;
        }
        return noCallState !== lastNoCallState;
    }

    function registerNextCalendarEventCheck(online) {
        if (!angular.isDefined(online)) online = RoomService.online();
        if (
            online &&
            room &&
            sutl.getNode(room, 'caps.hasCalendar', false) &&
            remoteControlCodeVisible() &&
            nextCalendarEventInterval === null
        ) {
            log.debug('Starting interval for next calendar event check');
            $timeout(checkNextCalendarEvent);
            nextCalendarEventInterval = $interval(
                checkNextCalendarEvent,
                nextCalendarEventCheckDurationMs
            );
        } else if (
            (!online ||
                (room && !sutl.getNode(room, 'caps.hasCalendar', false)) ||
                !remoteControlCodeVisible()) &&
            nextCalendarEventInterval !== null
        ) {
            log.debug('Stopping interval for next calendar event check');
            $interval.cancel(nextCalendarEventInterval);
            nextCalendarEventInterval = null;
        }
    }

    function remoteControlCodeVisible() {
        return (
            site &&
            site.$isLoaded() &&
            sutl.getNode(site, 'settings.remoteControlEnabled') === true &&
            KayleeService.isConnected() &&
            ((KayleeService.isAuthenticatedAsUser() &&
                !KayleeService.isAuthenticatedAsAnonymousRemoteControl()) ||
                KayleeService.isAuthenticatedAsController())
        );
    }

    function remoteControlUsersVisible() {
        if (!remoteControlSessions) return false;
        var sessionCount = remoteControlSessions.filter(function (session) {
            return session.expires > Date.now();
        }).length;
        // Check whether session count has dropped to zero after filtering for
        // expired sessions. Required to catch case in remote control browser
        // when session expires.
        if (sessionCount === 0 && previousRemoteControlSessionCount > 0) {
            if (!remoteControlCodeVisible() && processNoCallState()) {
                notifyListeners(noCallStateListeners, noCallState);
            }
        }
        previousRemoteControlSessionCount = sessionCount;
        return KayleeService.isConnected() && sessionCount > 0;
    }

    function removeListener(listeners, listener) {
        for (var i = 0; i < listeners.length; i++) {
            if (listeners[i] === listener) {
                listeners.splice(i, 1);
                break;
            }
        }
    }

    function setControlStateInitialized(initialized) {
        controlStateInitialized = initialized;
    }
}

module.exports = ViewStateService;
